/**
 * Thin client for the global-auth admin API (auth.eutochkin.com).
 *
 * Mirrors `api/client.ts` exactly but points at `VITE_AUTH_BACKEND_URL`
 * instead of the local `/api/v1` prefix. A 401 refreshes the session;
 * a valid 403 is surfaced to the screen without logging the operator out.
 * It otherwise keeps the same `ApiError` surface and Bearer storage.
 *
 * **CORS requirement**: the global-auth instance must add the admin
 * panel's origin to its `Access-Control-Allow-Origin` allow-list (the
 * `allowed_origins` array on its own Service entry — yes, the auth
 * server registers itself as a service in its own table). Without that,
 * every fetch here will fail in the browser before reaching network.
 */
import { ApiError } from './client';
import { clearTokens } from '@/auth/tokens';
import { refreshAccessToken } from '@/auth/refresh';
import { redirectToAuth } from '@/auth/redirect';
import { panelBearer, retryWithFreshOgToken } from '@/auth/session';
import { emitAuthorizationDenied } from '@/auth/authorizationEvents';
const authBase = () => import.meta.env.VITE_AUTH_BACKEND_URL;
async function doFetchAuth(path, init, attempt) {
    const base = authBase();
    if (!base) {
        throw new ApiError(0, null, 'VITE_AUTH_BACKEND_URL is not configured');
    }
    const headers = new Headers(init.headers);
    const bearer = panelBearer();
    if (bearer)
        headers.set('Authorization', `Bearer ${bearer.token}`);
    if (init.body !== undefined && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(base + path, {
        ...init,
        headers,
        body: init.body === undefined
            ? undefined
            : init.body instanceof FormData
                ? init.body
                : JSON.stringify(init.body),
    });
    if (bearer?.og && res.status === 401) {
        return retryWithFreshOgToken(res, attempt, () => doFetchAuth(path, init, 1));
    }
    if (res.status === 403) {
        if (path !== '/users/me' && path !== '/users/me/short') {
            emitAuthorizationDenied('panel');
        }
        return res;
    }
    // GlobalAuth uses 403 for a valid operator who lacks the required role.
    // Surface that response to the screen; refreshing or clearing the session
    // would turn an authorization error into a misleading logout loop.
    if (res.status === 401 && attempt === 0) {
        const newToken = await refreshAccessToken();
        if (newToken)
            return doFetchAuth(path, init, 1);
        clearTokens();
        redirectToAuth();
        throw new ApiError(res.status, null, 'Re-auth required');
    }
    if (res.status === 401 && attempt > 0) {
        clearTokens();
        redirectToAuth();
        throw new ApiError(res.status, null, 'Re-auth required');
    }
    return res;
}
export async function apiAuth(path, init = {}) {
    const res = await doFetchAuth(path, init, 0);
    const ct = res.headers.get('content-type') || '';
    const body = ct.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok)
        throw new ApiError(res.status, body, `${res.status} ${res.statusText}`);
    // 204 No Content on DELETE returns empty string; coerce to undefined-as-T.
    if (res.status === 204)
        return undefined;
    return body;
}
